/**
 * **濒死的裂缝遮罩** —— 从画布正中放射出去的碎裂线条。
 *
 * 评审：「给濒死状态再加一个自中心放射状碎裂线条的扰动，这些碎裂线条是类似于
 * card mask 的一个 mask，被遮住的标量场区域被重设为 0。」
 *
 * ## 它是遮罩，不是"画上去的线"
 *
 * 这一点决定了整个实现：
 *
 * ```
 *   白线画在透明底上  →  标量场采样它  →  gray *= 1 - crack
 * ```
 *
 * 所以屏幕上**看不见红线**，看见的是"网点在这些地方断开了" —— 一整块印刷面
 * 沿着裂缝碎开。它和卡牌占地是同一套机制（`render/cardFootprint.ts`），
 * 只是来源不同：那一张是每张牌的圆角矩形，这一张是从中心长出来的锯齿线。
 *
 * ⚠️ 因此它**不能**画在濒死层（`dying/layer.ts`）里 —— 那一层在卡牌**上面**，
 * 画上去就是"红线上盖了一层红色"，和评审要的"场被置零"正好相反。
 * 两层的分工：这一层改的是**背景的场**，濒死层是**叠在场上面的红光和横带**。
 *
 * ## 形状：种子化、看得出手长出来的
 *
 * 每一条裂缝是**从中心往外走的一串折线**：方向基本均匀分布，每一节加一点
 * 随机的横向偏移（锯齿），一直画到画布之外。全部由 `core/rng` 的种子化
 * PRNG 生成 —— 同一个种子必然长出同一张碎裂纹，于是"这个形状好不好看"是
 * 可以复现、可以调参数的（和 `ScalarField` 同一条纪律）。
 *
 * ⚠️ 生长是**逐条错开**的：十条裂缝同时长出来像一朵菊花，错开之后才像
 * "这里崩了、那里也崩了"。
 *
 * ## ⚠️ 生长 = 每帧多画一节，**不是把整条线放大**
 *
 * 评审：「背景裂痕出现的动画不应是尺寸逐渐放大出现，是"生长"，每帧逐步绘制
 * 更长的部分来实现」。
 *
 * 第一版按 `progress` 把**整条折线等比缩**（`total = 全长 × progress`，
 * 每节的步长跟着变小）。那看起来是"一条小裂缝原地涨大" —— 折线上的锯齿
 * 也跟着放大，读起来是**缩放**而不是**裂开**。
 *
 * 现在每一节的长度**固定**（`span / segments`），`progress` 只决定
 * **画到第几节**（最后一节按小数部分截断）。于是已经长出来的那一段，
 * 形状从第一帧起就定死了，后面只是往前接 —— 那才是"生长"。
 * 纯计算在 `crackPoints()` 里，测试直接钉这条区别。
 *
 * ## 时间轴：墙上时间，不是场时间
 *
 * 裂缝是"濒死这几秒里发生的事"，和背景的播放速度无关（标量场那条轴现在
 * 慢十倍）—— 所以它走 `ticker.deltaMS`，一秒就是一秒。
 */

import { Container, Graphics, RenderTexture, Sprite, type Renderer, type Ticker } from 'pixi.js';
import { keepAlive } from '../textureLifetime';

/** 可以边跑边改的那几个（调参页要拖滑块）。改了立刻重长一遍 */
export interface CrackStyle {
  /** 几条裂缝 */
  arms: number;
  /** 线宽（逻辑像素） */
  lineWidth: number;
  /** 长满要多久（秒） */
  growSeconds: number;
  /** 逐条错开（秒） */
  staggerSeconds: number;
  /** 合上要多久（秒） */
  fadeSeconds: number;
  /** 每条折线的节数 */
  segments: number;
}

export interface CrackMaskOptions {
  readonly renderer: Renderer;
  /** 画布逻辑尺寸（和卡牌占地同一套坐标，遮罩就是这么被采样的） */
  readonly width: number;
  readonly height: number;
  /** 几条裂缝 */
  readonly arms?: number;
  /** 线宽（逻辑像素）。它就是"场被置零的那一条有多宽" */
  readonly lineWidth?: number;
  /** 一条裂缝从中心长到边缘要多久（秒） */
  readonly growSeconds?: number;
  /** 相邻两条裂缝起跑的间隔（秒）。0 = 一起长 */
  readonly staggerSeconds?: number;
  /** 合上（强度降到 0）要多久（秒） */
  readonly fadeSeconds?: number;
  /** 每条裂缝折线的节数。越多越碎、越像玻璃 */
  readonly segments?: number;
}

/** 一条裂缝的静态形状：一串从中心往外的锯齿 */
export interface Arm {
  /** 方向（弧度） */
  readonly angle: number;
  /** 折线每一节的横向抖动（-1..1） */
  readonly jitter: readonly number[];
  /** 起跑延迟（0..1，乘 `staggerSeconds`） */
  readonly delay: number;
  /** 长度倍率。让几条特别长、几条短一截 */
  readonly reach: number;
}

export class CrackMask {
  /** 遮罩贴图。白线 = 这一块的场要置零。背景把它喂给标量场 */
  mask: RenderTexture;

  /** 把遮罩直接画出来看的那一层（调参页用）。**不进游戏画面** */
  readonly view: Sprite;

  readonly #renderer: Renderer;
  readonly #width: number;
  readonly #height: number;
  #lineWidth: number;
  #grow: number;
  #stagger: number;
  #fade: number;
  #segments: number;
  #arms: readonly Arm[];
  /**
   * **遮罩铺在哪一块**（画布逻辑坐标）。默认 `null` = 画布本身。
   *
   * 评审只报了横带那条（"红线没有延伸至画面边缘"），但裂缝是**同一个病的
   * 另一半**：场铺满整块视口，而裂缝只在画布那块矩形里把场置零 ——
   * 窗口不是 16:10 时两边黑边上会留着没裂开的网点，看着像贴图错位。
   * 所以贴图跟着视口一起长，见 `setViewport()`。
   */
  #quad: { x: number; y: number; w: number; h: number } | null = null;
  /** 重画用的画布。每帧 `clear()` 之后重画，不留上一帧的东西 */
  readonly #canvas = new Graphics();
  readonly #stage = new Container();

  /** 长出来用了多久（秒）。`begin()` 归零 */
  #age = 0;
  /** 当前强度 0..1（淡出用）。背景拿它当 uniform */
  #strength = 0;
  /** 目标强度。`begin()` → 1，`end()` → 0 */
  #target = 0;
  /** 上一次画出来时用的强度。没变就不重画 */
  #drawnAt = -1;
  /** 还有没有活干（生长中 / 强度还在动）。全静下来就不再重画 */
  #live = false;

  constructor(options: CrackMaskOptions) {
    this.#renderer = options.renderer;
    this.#width = Math.max(1, options.width);
    this.#height = Math.max(1, options.height);
    this.#lineWidth = options.lineWidth ?? 5;
    this.#grow = Math.max(0.02, options.growSeconds ?? 0.55);
    this.#stagger = Math.max(0, options.staggerSeconds ?? 0.16);
    this.#fade = Math.max(0.02, options.fadeSeconds ?? 0.5);
    this.#segments = Math.max(2, Math.round(options.segments ?? 14));
    this.#arms = makeArms(options.arms ?? 11, this.#segments);

    this.mask = makeMask(this.#width, this.#height);

    this.#stage.addChild(this.#canvas);
    // 一开始必须是**空的**：没在濒死时它一个像素都不该有
    this.#clear();

    this.view = new Sprite(this.mask);
    this.view.label = 'crack-mask';
    this.view.visible = false;
  }

  /** 当前强度 0..1。0 = 遮罩是空的（等于没接） */
  get strength(): number {
    return this.#strength;
  }

  /** 还有没有在动。调试与测试用 */
  get live(): boolean {
    return this.#live;
  }

  /** 这一局长了几条裂缝 */
  get armCount(): number {
    return this.#arms.length;
  }

  /** 现在的样式。调参页读它来初始化滑块 */
  get style(): CrackStyle {
    return {
      arms: this.#arms.length,
      lineWidth: this.#lineWidth,
      growSeconds: this.#grow,
      staggerSeconds: this.#stagger,
      fadeSeconds: this.#fade,
      segments: this.#segments,
    };
  }

  /**
   * 改样式并**立刻重长一遍**。
   *
   * ⚠️ 形状参数（条数 / 节数 / 种子）改了必须重算折线，而不只是重画 ——
   *    它们决定的是 `#arms` 里那些点，画的时候只是照着摆。
   */
  applyStyle(next: Partial<CrackStyle>): void {
    const merged: CrackStyle = { ...this.style, ...next };
    this.#lineWidth = Math.max(0.5, merged.lineWidth);
    this.#grow = Math.max(0.02, merged.growSeconds);
    this.#stagger = Math.max(0, merged.staggerSeconds);
    this.#fade = Math.max(0.02, merged.fadeSeconds);
    this.#segments = Math.max(2, Math.round(merged.segments));
    this.#arms = makeArms(merged.arms, this.#segments);
    this.#age = 0;
    this.#target = 1;
    this.#strength = 1;
    this.#live = true;
    this.#drawnAt = -1;
    this.#draw();
  }

  /**
   * **告诉它遮罩该铺在哪一块**（画布逻辑坐标；默认是画布本身）。
   *
   * 变了就把那张遮罩贴图 `resize` 到新尺寸再重画 —— 只有窗口比例变了才会发生，一次而已。
   *
   * ⚠️ 这里**不能**"销毁再新建"：`mask` 是渲染目标，销毁的那一刻它还挂在
   *    render target 的 BindGroup 上，Pixi 会为此刷
   *    `a 'textureSource' was destroyed while still bound to a shader`。
   *    复用同一张 `RenderTexture` 就绕开了 —— `resize` 换的是底层资源，
   *    `TextureSource` 本身没被销毁（见 `docs/background-shader.md` 的坑）。
   */
  setViewport(quad: { x: number; y: number; w: number; h: number }): void {
    const before = this.#quad;
    if (
      before !== null &&
      before.x === quad.x &&
      before.y === quad.y &&
      before.w === quad.w &&
      before.h === quad.h
    ) {
      return;
    }
    this.#quad = { ...quad };
    const [w, h] = maskSize(quad.w, quad.h);
    if (this.mask.width !== w || this.mask.height !== h) this.mask.resize(w, h);
    this.#drawnAt = -1;
    // 没在濒死（遮罩是空的）时也要把新贴图渲染一次，不然它是未初始化的
    if (this.#live) this.#draw();
    else this.#clear();
  }

  /** 进入濒死：从头开始长 */
  begin(): void {
    this.#age = 0;
    this.#target = 1;
    this.#strength = 1;
    this.#live = true;
    this.#drawnAt = -1;
    this.#draw();
  }

  /**
   * 离开濒死：开始合上。
   *
   * ⚠️ 不立刻擦干净 —— 那会看到裂缝"啪"地不见。合上是 `update` 的事。
   */
  end(): void {
    this.#target = 0;
    this.#live = true;
  }

  /** 立刻回到"没有裂缝"（销毁前、换局时） */
  reset(): void {
    this.#age = 0;
    this.#target = 0;
    this.#strength = 0;
    this.#live = false;
    this.#clear();
  }

  /**
   * 每帧推进。挂到**那唯一一个** ticker 回调上。
   *
   * 生长期间每帧重画（形状本来就在变），长满 + 强度不动之后就停下 ——
   * 一局里只有濒死那几秒需要它干活。
   */
  update(ticker: Ticker): void {
    if (!this.#live) return;
    const dt = Math.min(ticker.deltaMS / 1000, 0.1);

    // 生长阶段：最后一条起跑之后还要走完 `grow` 那么久
    const growEnd = this.#grow + this.#stagger;
    const growing = this.#age < growEnd;
    if (growing || this.#target > this.#strength) this.#age += dt;

    // 强度：起得快（形状那边也在长）、合得慢
    const seconds = this.#target > this.#strength ? 0.12 : this.#fade;
    const k = 1 - Math.exp(-dt / (seconds * 0.35));
    this.#strength += (this.#target - this.#strength) * k;

    if (growing || Math.abs(this.#strength - this.#drawnAt) > 0.004) {
      this.#draw();
    }

    if (this.#target === 0 && this.#strength <= 0.004) {
      this.#strength = 0;
      this.#live = false;
      this.#clear();
    }
  }

  destroy(): void {
    this.#stage.destroy({ children: true });
    this.view.destroy();
    this.mask.destroy(true);
  }

  /** 把贴图擦干净（不留上一局的裂纹） */
  #clear(): void {
    this.#canvas.clear();
    this.#drawnAt = 0;
    this.#renderer.render({ container: this.#stage, target: this.mask, clear: true });
  }

  /**
   * 按当前的 `#age` 重画一遍。
   *
   * 每帧重画十几条折线是**故意**的：裂缝的生长本来就是每帧都在变的形状，
   * 而十几条 `moveTo/lineTo`（最多两百来个点）在 CPU 上可以忽略。
   */
  #draw(): void {
    const g = this.#canvas;
    g.clear();

    // 贴图可能是"整块视口"那么大的，坐标要换算到贴图的局部空间：
    // 画面正中在贴图里的位置 = 画布中心 − 视口左上角
    const cx = this.#width / 2 - (this.#quad?.x ?? 0);
    const cy = this.#height / 2 - (this.#quad?.y ?? 0);
    // 中心到最远角的距离再留一点余量：裂缝要画到画布外面去，不能半路断掉。
    // ⚠️ 用**画布**的尺寸算（不是视口）：裂缝的长短不该随窗口比例变
    const reach = Math.hypot(this.#width, this.#height) * 0.62;

    for (const arm of this.#arms) {
      const local = (this.#age - arm.delay * this.#stagger) / this.#grow;
      const progress = local <= 0 ? 0 : local >= 1 ? 1 : local;
      if (progress <= 0) continue;

      // ⚠️ 每一节的长度由**全长**定，和 `progress` 无关 —— 见文件头
      const span = reach * arm.reach;
      const points = crackPoints(arm, progress, {
        segments: this.#segments,
        span,
        lineWidth: this.#lineWidth,
        cx,
        cy,
      });
      g.moveTo(cx, cy);
      for (const point of points) g.lineTo(point.x, point.y);
    }

    // ⚠️ 一次 `stroke()` 把所有裂缝描完（Pixi 会把当前路径整体描边）——
    //    分成十一条路径分别描边会贵十倍，而且接头处反而更容易出现断口
    g.stroke({ width: this.#lineWidth, color: 0xffffff, cap: 'round', join: 'round' });

    this.#renderer.render({ container: this.#stage, target: this.mask, clear: true });
    this.#drawnAt = this.#strength;
  }
}

/**
 * 造一张遮罩贴图。
 *
 * ⚠️ 开抗锯齿：裂缝的边缘会被"1 - mask"直接乘进场里，硬边就是一条锯齿状的
 * 黑缝 —— 放大看像渲染错误，不像裂缝。
 */
function makeMask(width: number, height: number): RenderTexture {
  const [w, h] = maskSize(width, height);
  return keepAlive(
    RenderTexture.create({
      width: w,
      height: h,
      resolution: 1,
      antialias: true,
      label: 'dying-cracks',
    }),
  );
}

/** 遮罩贴图的尺寸（至少 1×1，取整）。`makeMask` 与 `setViewport` 共用 */
function maskSize(width: number, height: number): [number, number] {
  return [Math.max(1, Math.round(width)), Math.max(1, Math.round(height))];
}

/** `crackPoints` 需要的几何参数。纯计算的输入，画和测共用 */
export interface CrackPointOptions {
  /** 每条折线的节数（= 长满时一共有几个转折点） */
  readonly segments: number;
  /** 这条裂缝长满时有多长（逻辑像素，从中心算起） */
  readonly span: number;
  /** 线宽（逻辑像素）。抖动是按它缩放的 */
  readonly lineWidth: number;
  /** 中心（逻辑像素） */
  readonly cx: number;
  readonly cy: number;
}

/**
 * **一条裂缝长到 `progress` 时的那串点。** 纯函数，`#draw()` 和测试都走它。
 *
 * 生长语义（评审点名）：`progress` 决定**画到第几节**，不决定每节多长。
 * 所以 `progress = 0.5` 得到的那串点，和 `progress = 1` 得到的前半段
 * **逐点相同** —— 后面只是多接了几节。反过来"等比缩放"的实现下，
 * 两串点的坐标会全都不一样（每一节都变短了）。
 */
export function crackPoints(
  arm: Arm,
  progress: number,
  o: CrackPointOptions,
): Array<{ readonly x: number; readonly y: number }> {
  const p = progress <= 0 ? 0 : progress >= 1 ? 1 : progress;
  const grown = p * o.segments;
  const whole = Math.floor(grown);
  const frac = grown - whole;
  const last = Math.min(o.segments, whole + (frac > 0 ? 1 : 0));
  if (last <= 0) return [];

  const step = o.span / o.segments;
  const cos = Math.cos(arm.angle);
  const sin = Math.sin(arm.angle);
  // 法线：抖动加在这上面
  const nx = -sin;
  const ny = cos;

  const out: Array<{ x: number; y: number }> = [];
  for (let i = 1; i <= last; i++) {
    // 最后一节按小数部分截断（这样生长是连续的，不是一格一格跳）
    const t = i === last && frac > 0 ? frac : 1;
    const along = step * (i - 1 + t);
    const jitter = (arm.jitter[i - 1] ?? 0) * o.lineWidth * 2.2;
    // ⚠️ 抖动**从中心往外放大**：根部不该偏出去（不然裂缝不像从一点裂开），
    //    末端的锯齿最明显
    const fade = 0.2 + 0.8 * ((i - 1 + t) / o.segments);
    out.push({
      x: o.cx + cos * along + nx * jitter * fade,
      y: o.cy + sin * along + ny * jitter * fade,
    });
  }
  return out;
}

/**
 * 长出这一局的裂缝形状。**种子化**：同一个种子必然得到同一张碎裂纹。
 *
 * 角度刻意**均匀铺开再加抖动**（`i / n * 2π`）而不是纯随机：纯随机会
 * 常常几条挤在同一个方向，看上去像"一坨"而不是"碎开"。
 */
export function crackArms(count: number, segments: number): readonly Arm[] {
  return makeArms(count, segments);
}

function makeArms(count: number, segments: number): readonly Arm[] {
  // ⚠️ 纯视效（见 `docs/onboarding.md` §4.4 第 5 条）：裂缝这条臂偏几度无所谓
  const float = (): number => Math.random();

  const n = Math.max(1, Math.round(count));
  const arms: Arm[] = [];
  for (let i = 0; i < n; i++) {
    const even = (i / n) * Math.PI * 2;
    const angle = even + (float() - 0.5) * (Math.PI / n) * 0.8;
    const jitter: number[] = [];
    for (let s = 0; s < segments; s++) jitter.push((float() - 0.5) * 2);
    arms.push({
      angle,
      jitter,
      delay: float(),
      reach: 0.72 + float() * 0.5,
    });
  }
  return arms;
}
